import { withStyles } from '@material-ui/core/styles';
import React, { useState, useCallback } from 'react';
import { UPDATE, Edit, useNotify, useRefresh } from 'react-admin';
import { FormSpy } from 'react-final-form';
import SimpleFormWithSave from '../../core/components/form/SimpleFormWithSave';
import { useSelector } from 'react-redux';

import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import FormTitleBar from '../../core/components/FormTitleBar';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import DocumentWatermarkSummary from './DocumentWatermarkSummary';
import dataProvider from '../../core/providers/IccUserDataProvider';
import EditToolbar from '../../core/components/edit/EditToolbar';
import { getInitials, removePathEnd } from '../../core/utils';
import {
  validateWatermarkName,
  getFormatFromValues
} from './DocumentWatermarkUtils';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const DocumentWatermarkTitle = ({ record }) => {
  return (
    <FormTitleBar
      name={record.profileName}
      creation={false}
      showAvatar={true}
      avatarContent={getInitials(record.profileName)}
    />
  );
};

const DocumentWatermarkEdit = props => {
  const {
    classes,
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    userProfile,
    ...rest
  } = props;
  const displayToolbar = props.match.isExact;
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();
  const record = useSelector(state =>
    state.admin.resources.documentwatermarks
      ? state.admin.resources.documentwatermarks.data[props.id]
      : null
  );

  const handleSubmit = useCallback(
    values => {
      if (
        values.watermarkDisplay === 'direction' &&
        values.alignment === 'NOT_SET'
      ) {
        notify('ra.notification.watermark_text_direction', { type: 'error' });
        return;
      }

      if (
        values.watermarkDisplay === 'position' &&
        values.orientation === 'NOT_SET'
      ) {
        notify('ra.notification.watermark_text_position', { type: 'error' });
        return;
      } // alignment and orientation cannot be used at the same time

      if (values.watermarkDisplay === 'direction') {
        values.orientation = 'NOT_SET'; // if REPEATING_DIAGONAL is selected, Stretched must be set to false

        if (values.alignment === 'REPEATING_DIAGONAL') {
          values.stretched = false;
        }
      } else {
        values.alignment = 'NOT_SET';
        values.stretched = false; // default value in case Position is used
      }

      values.format = getFormatFromValues(
        values.isBoldSelected,
        values.isItalicSelected
      );
      dataProvider(UPDATE, 'documentwatermarks', {
        data: values
      })
        .then(() => {
          refresh();
          notify('ra.notification.custom_updated_message');
        })
        .catch(notifyServerError);
    },
    [notify, refresh, notifyServerError]
  );

  return (
    <React.Fragment>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>
      <Edit
        {...rest}
        undoable={false}
        title={<DocumentWatermarkTitle />}
        classes={{
          noActions: classes.noActions
        }}
      >
        <SimpleFormWithSave
          toolbar={
            displayToolbar ? (
              <EditToolbar
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                onBeforeDelete={() => setWhenBlockNavigation(false)}
                {...rest}
              />
            ) : (
              <></>
            )
          }
          variant="standard"
          margin="normal"
          validate={values => {
            return values.profileName === record.profileName
              ? null
              : validateWatermarkName(values);
          }}
          onSave={handleSubmit}
          initialValues={{
            font: 'HELVETICA',
            fontSize: 8,
            alignment: 'UPPER_LEFT_TO_BOTTOM_RIGHT',
            stretched: false,
            orientation: 'TOP_LEFT',
            watermarkDisplay:
              record &&
              record.alignment &&
              (record.alignment === 'NOT_SET' ? 'position' : 'direction'),
            isBoldSelected:
              record &&
              record.format &&
              (record.format === 'BOLD' ||
                record.format === 'BOLD_AND_ITALICS'),
            isItalicSelected:
              record &&
              record.format &&
              (record.format === 'ITALICS' ||
                record.format === 'BOLD_AND_ITALICS')
          }}
        >
          <DocumentWatermarkSummary />
          <FormSpy
            subscription={{
              dirty: true
            }}
            onChange={props => {
              setWhenBlockNavigation(props.dirty);
            }}
          />
        </SimpleFormWithSave>
      </Edit>
    </React.Fragment>
  );
};

export default withStyles(styles)(DocumentWatermarkEdit);
